import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'
import { getAllOrderDocumentTypes } from '../../../api/uploadDocuments/get-all-order-document-types'
import { createOrderDocumentType } from '../../../api/uploadDocuments/create-order-document-type'
import { getAllDocumentsByOrderId } from '../../../api/uploadDocuments/get-all-documents-by-orderId'
import { geturlForUploadOrderDocument } from '../../../api/uploadDocuments/get-url-for-upload-order-document'
import { downloadDocumentByOrderIdAndDocumentType } from '../../../api/uploadDocuments/download-document'
import { postSaveDocumentUploadRecord } from '../../../api/uploadDocuments/save-upload-document-record'

const initialState = {
  isFetchingOrderDocumentTypes: false,
  isUploadingOrderDocumentTypes: false,
  isUploadingOrderDocument: false,
  isFetchingOrderDocuments: false,
  fetchOrderDocumentTypesError: null,
  uploadOrderDocumentTypesError: null,
  uploadOrderDocumentError: null,
  recordSaveError: null,
  fetchOrderDocumentsError: null,
  orderDocumentTypes: [],
  crearedOrderDocumentType: null,
  uploadedOrderDocument: null,
  orderDocuments: [],
  isDownloadingOrderDocument: false,
  downloadOrderDocumentError: null,
  downloadedOrderDocument: null,
}

export const fetchOrderDocumentTypes = createAsyncThunk(
  'orderDocument/fetchOrderDocumentTypes',
  async (args, { rejectWithValue }) => {
    try {
      const response = await getAllOrderDocumentTypes()
      return response?.data?.data
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

export const postOrderDocumentType = createAsyncThunk(
  'orderDocument/createOrderDocumentType',
  async (args, { rejectWithValue }) => {
    try {
      const response = await createOrderDocumentType(args)
      return response?.data?.data
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

export const saveDocumentUploadRecord = createAsyncThunk(
  'orderDocument/saveDocumentUploadRecord',
  async (args, { rejectWithValue }) => {
    try {
      const response = await postSaveDocumentUploadRecord(args)
      return response?.data?.data
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

export const getUrlForUploadOrderDocument = createAsyncThunk(
  'orderDocument/getUrlForUploadOrderDocument',
  async (args, { rejectWithValue }) => {
    try {
      const response = await geturlForUploadOrderDocument(args)
      return response?.data?.data
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

export const fetchOrderDocumentsByOrderId = createAsyncThunk(
  'orderDocument/fetchOrderDocumentsByOrderId',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await getAllDocumentsByOrderId(orderId)
      return response?.data?.data?.orderDocuments
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

export const fetchDownloadOrderDocument = createAsyncThunk(
  'orderDocument/downloadOrderDocument',
  async (args, { rejectWithValue }) => {
    try {
      const response = await downloadDocumentByOrderIdAndDocumentType(args)
      return response?.data
    } catch (exception) {
      return rejectWithValue(exception)
    }
  },
)

const orderDocumentTypesSlice = createSlice({
  name: 'orderDocument',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all order document types
    builder
      .addCase(fetchOrderDocumentTypes.pending, (state) => {
        state.isFetchingOrderDocumentTypes = true
        state.fetchOrderDocumentTypesError = null
        state.orderDocumentTypes = []
      })
      .addCase(fetchOrderDocumentTypes.fulfilled, (state, action) => {
        state.isFetchingOrderDocumentTypes = false
        state.orderDocumentTypes = action.payload
        state.fetchOrderDocumentTypesError = null
      })
      .addCase(fetchOrderDocumentTypes.rejected, (state, action) => {
        state.isFetchingOrderDocumentTypes = false
        state.fetchOrderDocumentTypesError = action.payload
        state.orderDocumentTypes = []
      })
      // create order document type
      .addCase(postOrderDocumentType.pending, (state) => {
        state.isUploadingOrderDocumentTypes = true
        state.uploadOrderDocumentTypesError = null
        state.crearedOrderDocumentType = null
      })
      .addCase(postOrderDocumentType.fulfilled, (state, action) => {
        state.isUploadingOrderDocumentTypes = false
        state.crearedOrderDocumentType = action.payload
        state.uploadOrderDocumentTypesError = null
      })
      .addCase(postOrderDocumentType.rejected, (state, action) => {
        state.isUploadingOrderDocumentTypes = false
        state.uploadOrderDocumentTypesError = action.payload
        state.crearedOrderDocumentType = null
      })
      // upload order document
      .addCase(getUrlForUploadOrderDocument.pending, (state) => {
        state.isUploadingOrderDocument = true
        state.uploadOrderDocumentError = null
        state.uploadedOrderDocument = null
      })
      .addCase(getUrlForUploadOrderDocument.fulfilled, (state, action) => {
        state.isUploadingOrderDocument = false
        state.uploadedOrderDocument = action.payload
        state.uploadOrderDocumentError = null
      })
      .addCase(getUrlForUploadOrderDocument.rejected, (state, action) => {
        state.isUploadingOrderDocument = false
        state.uploadOrderDocumentError = action.payload
        state.uploadedOrderDocument = null
      })
      // save document upload record
      .addCase(saveDocumentUploadRecord.pending, (state) => {
        state.isUploadingOrderDocument = true
        state.uploadOrderDocumentError = null
        state.uploadedOrderDocument = null
        state.recordSaveError = null
      })
      .addCase(saveDocumentUploadRecord.fulfilled, (state, action) => {
        state.isUploadingOrderDocument = false
        state.uploadedOrderDocument = action.payload
        state.uploadOrderDocumentError = null
        state.recordSaveError = null
      })
      .addCase(saveDocumentUploadRecord.rejected, (state, action) => {
        state.isUploadingOrderDocument = false
        state.uploadOrderDocumentError = null
        state.uploadedOrderDocument = null
        state.recordSaveError = action.payload
      })
      // fetch order documents by order id
      .addCase(fetchOrderDocumentsByOrderId.pending, (state) => {
        state.isFetchingOrderDocuments = true
        state.fetchOrderDocumentsError = null
        state.orderDocuments = []
      })
      .addCase(fetchOrderDocumentsByOrderId.fulfilled, (state, action) => {
        state.isFetchingOrderDocuments = false
        state.orderDocuments = action.payload
        state.fetchOrderDocumentsError = null
      })
      .addCase(fetchOrderDocumentsByOrderId.rejected, (state, action) => {
        state.isFetchingOrderDocuments = false
        state.fetchOrderDocumentsError = action.payload
        state.orderDocuments = []
      })
      // download order document
      .addCase(fetchDownloadOrderDocument.pending, (state) => {
        state.isDownloadingOrderDocument = true
        state.downloadOrderDocumentError = null
        state.downloadedOrderDocument = null
      })
      .addCase(fetchDownloadOrderDocument.fulfilled, (state, action) => {
        state.isDownloadingOrderDocument = false
        state.downloadedOrderDocument = action.payload
        state.downloadOrderDocumentError = null
      })
      .addCase(fetchDownloadOrderDocument.rejected, (state, action) => {
        state.isDownloadingOrderDocument = false
        state.downloadOrderDocumentError = action.payload
        state.downloadedOrderDocument = null
      })
      /* Action which gets triggered
         when running persistor.purge() */
      .addCase(PURGE, () => {
        return initialState
      })
  },
})

export default orderDocumentTypesSlice.reducer
